import type { StoreRole } from "./business-member";

/**
 * The ten areas of the platform sketch's "Behörighet" matrix (GRA-194). Each
 * id is also the i18n key under `shell.json#access.matrix.rows.<id>` on the
 * frontend — keep the two in sync when adding an area.
 */
export const ACCESS_AREAS = [
  "storeKpis",
  "localGoals",
  "profiles",
  "managerNotes",
  "checkIn",
  "loginData",
  "talkPrep",
  "recommendations",
  "anser",
  "access",
] as const;
export type AccessArea = (typeof ACCESS_AREAS)[number];

/**
 * How much of an area a role sees. `"none"` is the only value a server guard
 * acts on today (see `canAccessArea`); the rest are presentation detail the
 * existing intelligence-visibility scoping (team/member subtree) already
 * narrows down to. `"change"` marks the one cell that grants a write (editing
 * roles and area ownership on the Behörighet page itself).
 */
export type AccessMatrixValue =
  | "all"
  | "ownDept"
  | "onlyOwn"
  | "aggregated"
  | "own"
  | "basic"
  | "ownPlusDeptKpi"
  | "change"
  | "none";

export type AccessMatrixRow = { id: AccessArea; values: Record<StoreRole, AccessMatrixValue> };

/**
 * "Vad man ser": the sketch's matrix, one row per area, one column per
 * store role. Single source of truth for both the `AccessMatrix` UI
 * (apps/graftus-webapp-2) and the tRPC capability guard
 * (apps/business-api) — do not duplicate this table elsewhere.
 */
export const ACCESS_MATRIX: readonly AccessMatrixRow[] = [
  { id: "storeKpis", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "basic" } },
  {
    id: "localGoals",
    values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "ownPlusDeptKpi" },
  },
  { id: "profiles", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "onlyOwn" } },
  { id: "managerNotes", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "none" } },
  {
    id: "checkIn",
    values: { super: "aggregated", adminchef: "aggregated", admin: "aggregated", medarb: "own" },
  },
  { id: "loginData", values: { super: "all", adminchef: "all", admin: "none", medarb: "none" } },
  { id: "talkPrep", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "own" } },
  { id: "recommendations", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "own" } },
  { id: "anser", values: { super: "all", adminchef: "all", admin: "ownDept", medarb: "basic" } },
  { id: "access", values: { super: "change", adminchef: "none", admin: "none", medarb: "none" } },
];

const MATRIX_BY_AREA: ReadonlyMap<AccessArea, AccessMatrixRow> = new Map(
  ACCESS_MATRIX.map((row) => [row.id, row]),
);

/** The matrix cell for `area`/`role`. Throws on an unknown area — a programmer error, never user input. */
export function accessMatrixValue(area: AccessArea, role: StoreRole): AccessMatrixValue {
  const row = MATRIX_BY_AREA.get(area);
  if (!row) throw new Error(`Unknown access area: ${area}`);
  return row.values[role];
}

/**
 * The binary gate a server guard enforces: `false` only for the matrix's
 * `"none"` cells. Every other value still sees *something* in the area — how
 * much is the existing intelligence-visibility (team/member subtree) scoping,
 * not this matrix.
 */
export function canAccessArea(area: AccessArea, role: StoreRole): boolean {
  return accessMatrixValue(area, role) !== "none";
}

/** True for the matrix's one write cell — changing roles or area ownership. */
export function canChangeArea(area: AccessArea, role: StoreRole): boolean {
  return accessMatrixValue(area, role) === "change";
}
